/**
 * The 30-minute-grid time slots a host may pick when adding an upsell offer row (spec S2),
 * filtered to the correct side of the standard time (the same fence
 * {@code StayExtensionStair}/{@code addOffer} enforce on the backend, applied here too so an
 * impossible row cannot even be typed).
 *
 * <p>Pulled out of {@link ../components/UpsellSettings} so it can be unit tested without that
 * file's API import (and, transitively, axios — this project's Jest/CRA setup cannot parse it as
 * an ES module, unrelated to this feature).
 *
 * <p>Compares full minutes-of-day rather than just the hour, which matters once the standard time
 * itself can be a half-hour value (e.g. 16:30): an hour-only comparison would wrongly admit or
 * reject the slot that shares that hour.
 */
export const toMinutesOfDay = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m || 0);
};

export const minutesOfDayToTime = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/**
 * Every half-hour slot (00:00, 00:30, 01:00, …, 23:30) on the correct side of `standardTime`.
 *
 * <p>`allowMidnight` is spec S5: 00:00 is nonsensical for a room check-in/check-out type (nobody
 * checks in or out at midnight) but a real, acceptable value for luggage — pass
 * `isLuggage(type)` from `utils/stayExtensionTypes`. The backend (`addOffer`) enforces the same
 * rule; this just keeps the dropdown from offering a value the save will reject.
 */
export const offerSlotMinutes = (standardTime: string, isEarly: boolean, allowMidnight: boolean): number[] => {
  const standardMinutes = toMinutesOfDay(standardTime);
  return Array.from({ length: 48 }, (_, i) => i * 30)
    .filter(minutes => (isEarly ? minutes < standardMinutes : minutes > standardMinutes))
    .filter(minutes => allowMidnight || minutes !== 0);
};
